// src/components/Dashboard/PlayerDashboard/AnnouncementsWidget.jsx
// Shows recent announcements from programs and coaches

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { FaBullhorn, FaClock, FaArrowRight } from 'react-icons/fa';
import styles from './AnnouncementsWidget.module.css';

const AnnouncementsWidget = ({ programs, selectedProgramId, refreshTrigger }) => {
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch announcements
  useEffect(() => {
    const fetchAnnouncements = async () => {
      if (!programs || programs.length === 0) {
        setAnnouncements([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const auth = getAuth();
        const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        
        if (!token) {
          setLoading(false);
          return;
        }

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        // Fetch notifications/announcements for each program
        const allAnnouncements = [];
        for (const program of programs) {
          try {
            const response = await fetch(
              `${backendUrl}/api/notifications/program/${program._id}?limit=5`, 
              { headers }
            );
            if (response.ok) {
              const data = await response.json();
              const notifications = Array.isArray(data) ? data : data.notifications || [];
              notifications.forEach(notification => {
                allAnnouncements.push({
                  ...notification,
                  programId: program._id,
                  programName: program.name
                });
              });
            }
          } catch (error) {
            // Skip failed fetches
          }
        }

        // Sort by date (newest first)
        allAnnouncements.sort((a, b) => 
          new Date(b.createdAt || b.sentAt) - new Date(a.createdAt || a.sentAt)
        );
        setAnnouncements(allAnnouncements);
      } catch (error) {
        console.error('Error fetching announcements:', error);
        setAnnouncements([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncements();
  }, [programs, refreshTrigger]);

  // Filter by selected program
  const filteredAnnouncements = selectedProgramId === 'all' 
    ? announcements 
    : announcements.filter(a => a.programId === selectedProgramId);

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleViewAll = () => {
    navigate('/notifications');
  };

  // Loading state
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <FaBullhorn className={styles.headerIcon} />
            <h3 className={styles.headerTitle}>Announcements</h3>
          </div>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <FaBullhorn className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>Announcements</h3>
        </div>
        {filteredAnnouncements.length > 0 && (
          <span className={styles.count}>{filteredAnnouncements.length}</span>
        )}
      </div>

      <div className={styles.content}>
        {filteredAnnouncements.length === 0 ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}>📢</span>
            <p>No announcements</p>
            <span className={styles.emptyHint}>Messages from your programs will appear here</span>
          </div>
        ) : (
          <>
            <div className={styles.announcementsList}>
              {filteredAnnouncements.slice(0, 3).map((announcement, index) => (
                <div key={announcement._id || index} className={styles.announcementCard}>
                  <div className={styles.announcementContent}>
                    <p className={styles.announcementText}>
                      {announcement.message || announcement.title || announcement.content}
                    </p>
                    <div className={styles.announcementMeta}>
                      <span className={styles.announcementTime}>
                        <FaClock /> {formatTime(announcement.createdAt || announcement.sentAt)}
                      </span>
                      {announcement.programName && (
                        <span className={styles.announcementProgram}>
                          • {announcement.programName}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredAnnouncements.length > 3 && (
              <button className={styles.viewAllBtn} onClick={handleViewAll}>
                View all messages <FaArrowRight />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AnnouncementsWidget;



